import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux'
import * as Sentry from '@sentry/react';
import { selectConfigExpressSettingsName } from '../../../../store/slices/config/configSelectors';
import apiAccess from '../../../../api/apiAccess';
import { selectCurrentUserEmail } from '../../../../store/slices/auth/authSelectors';
import { selectPaymentID, selectPaymentSignatory } from '../../../../store/slices/payment/paymentSelectors';
import { Button, Grid, Row, Col } from 'rsuite';
import { Text } from 'rsuite';
import { convertNumberToCurrency } from '../../../../common/utils';
import IconIndicator from '../../../user-feedback/IconIndicator';
import Notice from '../../../user-feedback/Notice';

export const ACHBankTransfer = ({ clientPaymentInfo, loading, city, onPay, error }) => {
    const cityName = useSelector(selectConfigExpressSettingsName);
    const currentUserEmail = useSelector(selectCurrentUserEmail);
    const signatoryName = useSelector(selectPaymentSignatory);
    const paymentID = useSelector(selectPaymentID);

    const [paymentDetails, setPaymentDetails] = useState(undefined);
    const [hasData, setHasData] = useState(true);
    const [inProgress, setInProgress] = useState(true);
    const buttonDisabled = useMemo(() => {
        return !hasData || inProgress || loading;
    }, [loading]);

    useEffect(() => {
        setHasData(cityName && currentUserEmail && signatoryName && paymentID);
    }, [cityName, currentUserEmail, signatoryName, paymentID]);

    useEffect(() => {
        const fetchACHBankDetails = async () => {
            if (currentUserEmail && signatoryName && paymentID) {
                setInProgress(true);
                try {
                    const pushACHBankDetails = await apiAccess.payByACHCredit(paymentID, signatoryName, currentUserEmail);
                    setPaymentDetails(pushACHBankDetails);
                } catch (err) {
                    //console.error('Error fetching ACH bank details:', err);
                    Sentry.captureException(err);
                } finally {
                    setInProgress(false);
                }
            }
        };

        fetchACHBankDetails();
    }, [currentUserEmail, signatoryName, paymentID]);

    const handleClick = () => {
        if (!buttonDisabled) {
            onPay();
        }
    };

    return (
        <Grid fluid>
            <Row className='mb-16'>
                <Col xs={24}>
                    <Text>
                        Please go to your bank and send the payment amount to the {city} account utilizing these details:
                    </Text>
                </Col>
            </Row>
            <Row className='mb-4'>
                <Col xs={12}>
                    <Text>Amount to send:</Text>
                </Col>
                <Col xs={12}>
                    <Text>{convertNumberToCurrency(clientPaymentInfo?.total_amount_due)}</Text>
                </Col>
            </Row>
            <Row className='mb-4'>
                <Col xs={12}>
                    <Text>Send funds to:</Text>
                </Col>
                <Col xs={12}>
                    <Text>{clientPaymentInfo?.bank_name}</Text>
                </Col>
            </Row>
            <Row className='mb-4'>
                <Col xs={12}>
                    <Text>Routing number:</Text>
                </Col>
                <Col xs={12}>
                    <Text>{clientPaymentInfo?.routing_number}</Text>
                </Col>
            </Row>
            <Row className='mb-16'>
                <Col xs={12}>
                    <Text>Account number:</Text>
                </Col>
                <Col xs={12}>
                    <Text>{clientPaymentInfo?.account_number}</Text>
                </Col>
            </Row>
            <Row className='mb-16'>
                <Col xs={24}>
                    <Text>
                        Please complete your payment within 24 hours of confirming this payment method to avoid late fees.
                    </Text>
                </Col>
            </Row>
            {error && (
                <Row className='mb-16'>
                    <Col xs={24}>
                        <Notice message={error} />
                    </Col>
                </Row>
            )}
            {loading && (
                <Row className='mb-16'>
                    <Col xs={24}>
                        <IconIndicator />
                    </Col>
                </Row>
            )}
            <Row>
                <Col xs={24}>
                    <Button className='btn-payment' disabled={buttonDisabled} onClick={handleClick}>
                        Select ACH Bank Transfer as your payment method
                    </Button>
                </Col>
            </Row>
        </Grid>
    );
};

export default ACHBankTransfer;
